import React, { useState } from 'react'
import { useIntl, WithIntlProps } from 'react-intl'
import { Modal, Form, InputNumber, Radio, Typography, Tooltip, Divider, Alert } from 'antd'
import { CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

const { Paragraph, Text } = Typography

import { getLeaveDaysNumber } from '../utils/leave-quotas'
import { isFullDayInterval } from '../utils/interval'
import { calculateProratedQuota, roundTo2Decimals, roundTo4Decimals } from '../../functions/calculation-shared'
import { displayLeaveInDaysAndHours } from '../display-leave-in-days-and-hours'
import { AccrualTypeEnum } from '@vacationtracker/shared/types/leave-policy'

import { IMyProfileLeaveType, IUserLeaveTypeResult } from '../../../frontend/src/types/custom-queries'
import { LocaleEnum } from '../../types/i18n'
import { isBefore } from 'date-fns'
import { IWorkingSchedule, WorkingHours } from '../../types/work-week'
import { IUserLeaveTypesUpdateEvent } from '../../types/leave-type'

interface IUserLeaveTypeForm {
  onSave: (event: IUserLeaveTypesUpdateEvent) => void
  handleCancel: (e) => void
  visible: boolean
  leaveType: IUserLeaveTypeResult | IMyProfileLeaveType
  dateRange: {
    name: string
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  IntlMessages: React.FC<WithIntlProps<any>> & { WrappedComponent: React.ComponentType<any> }
  hourlyLeaveAccounting: boolean
  userStartDate?: string
  locationResetQuotas?: string
  userName?: string
  workingHours?: WorkingHours | IWorkingSchedule
  period?: {
    start: string
    end: string
  }
  locale?: LocaleEnum
  isInitialPeriod: boolean
}

const UserLeaveTypeForm = ({
  onSave,
  handleCancel,
  visible,
  leaveType,
  dateRange,
  IntlMessages,
  hourlyLeaveAccounting,
  userStartDate,
  locationResetQuotas,
  userName,
  workingHours,
  period,
  locale,
  isInitialPeriod,
}: IUserLeaveTypeForm): React.ReactElement => {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const [hasUnlimitedDays, setUnlimitedDays] = useState(leaveType.hasUnlimitedDays || false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [showQuotaAdditionalSummary, setShowQuotaAdditionalSummary] = useState(false)
  const [yearlyQuota, setYearlyQuota] = useState(hourlyLeaveAccounting ? leaveType.quotas.baseQuota.raw.hours : leaveType.quotas.baseQuota.raw.days)
  const [seniorityEntitlementPerYear, setSeniorityEntitlementPerYear] = useState(hourlyLeaveAccounting
    ? leaveType.quotas.seniorityEntitlementForYear.raw.hours
    : leaveType.quotas.seniorityEntitlementForYear.raw.days
  )
  const [entitlementByRoleForYear, setEntitlementByRoleForYear] = useState(hourlyLeaveAccounting
    ? leaveType.quotas.entitlementByRoleForYear.raw.hours
    : leaveType.quotas.entitlementByRoleForYear.raw.days
  )
  const [shouldProrate, setShouldProrate] = useState(leaveType.shouldProrate)
  const [applyToFutureYears, setApplyToFutureYears] = useState(false)
  const defaultValue = {
    quota: roundTo2Decimals(yearlyQuota),
    seniorityEntitlementDaysForYear: roundTo2Decimals(seniorityEntitlementPerYear),
    entitlementByRoleDaysForYear: roundTo2Decimals(entitlementByRoleForYear),
    hasUnlimitedDays: leaveType.hasUnlimitedDays,
    applyToFutureYears,
    shouldProrate: leaveType.shouldProrate,
    toilDays: roundTo2Decimals(hourlyLeaveAccounting ? leaveType.quotas.toil.raw.hours : leaveType.quotas.toil.raw.days),
  }

  const onFinish = async () => {
    setConfirmLoading(true)
    try {
      const values = await form.validateFields()
      const data: IUserLeaveTypesUpdateEvent = {
        leaveTypeId: leaveType.leaveTypeId,
        ignoreRolloverFromPreviousYear: leaveType.ignoreRolloverFromPreviousYear,
        ...values,
        quotaInUnits: values.quota,
        unit: hourlyLeaveAccounting ? 'hours' : 'days',
        seniorityEntitlementForYear: values.seniorityEntitlementDaysForYear,
        entitlementByRoleForYear: values.entitlementByRoleDaysForYear,
      }
      onSave(data)
      setConfirmLoading(false)
    } catch(err) {
      setConfirmLoading(false)
    }
  }

  const onChangeShouldProrate = () => {
    setShouldProrate(!shouldProrate)
    setShowQuotaAdditionalSummary(true)
  }

  const onQuotaChange = (quota: number) => {
    form.setFieldsValue({
      quota,
    })
    setShowQuotaAdditionalSummary(true)
    setYearlyQuota(quota)
  }
  
  const onChangeSeniorityEntitlementDaysPerYear = (newValue: number) => {
    // For Seniority Entitlement we want to allow decimal values, SBE can be prorated in that case we need to allow decimal values
    const normalizedValue = Math.round(newValue * 100) / 100

    // if we track leaves in hours then we need to prevent steps different than 0.5
    const value = hourlyLeaveAccounting ? Math.round(normalizedValue * 2) / 2 : normalizedValue

    form.setFieldsValue({
      seniorityEntitlementDaysForYear: value,
    })
    setShowQuotaAdditionalSummary(true)
    setSeniorityEntitlementPerYear(normalizedValue)
  }
  
  const onChangeEntitlementByRoleDaysPerYear = (newValue: number) => {
    const normalizedValue = getLeaveDaysNumber(leaveType.accrualType, leaveType.allowHalfDays, leaveType.shortestLeaveInterval, newValue, hourlyLeaveAccounting)

    // if we track leaves in hours then we need to prevent decimal values
    const value = hourlyLeaveAccounting ? Math.trunc(normalizedValue) : normalizedValue

    form.setFieldsValue({
      entitlementByRoleDaysForYear: value,
    })
    setShowQuotaAdditionalSummary(true)
    setEntitlementByRoleForYear(normalizedValue)
  }

  const onChangeApplyToFutureYears = () => {
    setApplyToFutureYears(!applyToFutureYears)
    setShowQuotaAdditionalSummary(true)
  }

  const onChangeUnlimitedDays = (e) => {
    setUnlimitedDays(e.target.value)
    setShowQuotaAdditionalSummary(true)
  }

  const formItemLayout = {
    labelCol: { span: 18 },
    wrapperCol: { span: 6 },
  }

  const getProratedPreciseQuota = (convert = true) => {
    const calculated = calculateProratedQuota({
      quota: {
        unit: hourlyLeaveAccounting ? 'hours' : 'days',
        value: yearlyQuota,
      },
      yearStart: dayjs(period?.start).utc().toDate(),
      userStartDate: dayjs(userStartDate).utc().toDate(),
    })

    return {
      proratedQuota: displayLeaveInDaysAndHours({
        hourlyLeaveAccounting: !convert,
        value: calculated.value,
        formatMessage,
        workingHours: workingHours as WorkingHours,
      }),
      // We do not need to show the exact value in hours because the prorated value is always the same as the precise hourly quota (it has 2 decimal points)
      preciseQuota: hourlyLeaveAccounting ? undefined : roundTo4Decimals(calculated.value),
    }
  }

  return (
    <Modal
      title={<IntlMessages id="components.userLeaveTypeForm.updateUserLeaveType" values={{ name: leaveType.leaveTypeName }}  />}
      open={visible}
      closeIcon={<CloseCircleOutlined />}
      confirmLoading={confirmLoading}
      okText={<IntlMessages id="app.save" />}
      onOk={onFinish}
      okButtonProps={{ disabled: !showQuotaAdditionalSummary }}
      width={600}
      cancelText={<IntlMessages id="app.cancel" />}
      onCancel={handleCancel}
    >
      <Form
        key="userUpdateForm"
        form={form}
        name="userUpdateForm"
        layout="horizontal"
        onFinish={onFinish}
        initialValues={defaultValue}
        labelWrap={true}
      >
        <Paragraph>
          <IntlMessages id="components.userLeaveTypeForm.leaveTypeEditInfo" values={{ leaveTypeName: leaveType.leaveTypeName, dateRangeName: dateRange.name}} />
        </Paragraph>
        {leaveType.accrualType === AccrualTypeEnum.none &&
          <Form.Item
            label={<IntlMessages id="components.userLeaveTypeForm.hasUnlimitedDays" />}
            name="hasUnlimitedDays"
            {...formItemLayout}
          >
            <Radio.Group
              options={[
                { label: <IntlMessages id="app.yes" />, value: true },
                { label: <IntlMessages id="app.no" />, value: false },
              ]}
              optionType="button"
              onChange={onChangeUnlimitedDays}
            />
          </Form.Item>
        }

        {(!hasUnlimitedDays || (leaveType.accrualType !== AccrualTypeEnum.none && hasUnlimitedDays)) &&
          <>
            <Form.Item
              label={
                <>
                  <IntlMessages id="components.userLeaveTypeForm.quotaBase" />
                  <Tooltip className='info-tooltip' title={<IntlMessages id="components.userLeaveTypeForm.quotaBaseTooltip" />}><InfoCircleOutlined /></Tooltip>
                </>
              }
              name="quota"
              {...formItemLayout}
              rules={[{ required: true, message: <IntlMessages id="error.fieldIsRequired" /> }]}
            >
              <InputNumber
                style={{width: '140px'}}
                step={isFullDayInterval(leaveType) || hourlyLeaveAccounting ? 1 : 0.5}
                min={0}
                max={hourlyLeaveAccounting ? 2920 : 365}
                onChange={(value) => onQuotaChange(value as number)}
                disabled={hasUnlimitedDays && leaveType.accrualType === AccrualTypeEnum.none}
                addonAfter={<Text><IntlMessages id={`app.${hourlyLeaveAccounting ? 'hours' : 'days'}`}/></Text>}
              />
            </Form.Item>

            {new Date(period?.end as string).getFullYear() >= new Date().getFullYear() && 
              <Form.Item
                label={<IntlMessages id="components.userLeaveTypeForm.applyForFutureYears" />}
                name="applyToFutureYears"
                {...formItemLayout}
              >
                <Radio.Group
                  onChange={onChangeApplyToFutureYears}
                  options={[
                    { label: <IntlMessages id="app.yes" />, value: true },
                    { label: <IntlMessages id="app.no" />, value: false },
                  ]}
                  optionType="button"
                />
              </Form.Item>
            }
            <>
              <Form.Item
                label={
                  <>
                    <IntlMessages id="components.userLeaveTypeForm.quotaAdditionalSeniority" />
                    <Tooltip className='info-tooltip' 
                      title={
                        <IntlMessages 
                          id="components.userLeaveTypeForm.quotaAdditionalSeniorityInfo"
                          values={{ leaveTypeName: leaveType.leaveTypeName }}
                        />
                      }
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </>
                }
                name="seniorityEntitlementDaysForYear"
                {...formItemLayout}
              >
                <InputNumber
                  style={{width: '140px'}}
                  step={isFullDayInterval(leaveType) || hourlyLeaveAccounting ? 1 : 0.5}
                  min={0}
                  precision={2}
                  max={hourlyLeaveAccounting ? 2920 : 365}
                  onChange={(value) => onChangeSeniorityEntitlementDaysPerYear(value as number)}
                  addonAfter={<Text><IntlMessages id={`app.${hourlyLeaveAccounting ? 'hours' : 'days'}`}/></Text>}
                  // We don’t charge for add-ons anymore for the Complete plan and want to allow users to edit the seniority entitlement 
                  // even if it is deleted in the future or if the company is on the Core plan.
                  disabled={!leaveType.senioirtyEntitlementExists}
                />
              </Form.Item>
              <Form.Item
                label={
                  <>
                    <IntlMessages id="components.userLeaveTypeForm.quotaAdditionalRole" />
                    <Tooltip className='info-tooltip' title={<IntlMessages id="components.userLeaveTypeForm.quotaAdditionalRoleInfo" />}><InfoCircleOutlined /></Tooltip>
                  </>
                }
                name="entitlementByRoleDaysForYear"
                {...formItemLayout}
                rules={[{ required: false, message: <IntlMessages id="error.fieldIsRequired" /> }]}
              >
                <InputNumber
                  style={{width: '140px'}}
                  step={isFullDayInterval(leaveType) || hourlyLeaveAccounting ? 1 : 0.5}
                  min={0}
                  max={hourlyLeaveAccounting ? 2920 : 365}
                  precision={2}
                  addonAfter={<Text><IntlMessages id={`app.${hourlyLeaveAccounting ? 'hours' : 'days'}`}/></Text>}
                  disabled={!leaveType.entitlementByRoleExists}
                  onChange={(value) => onChangeEntitlementByRoleDaysPerYear(value as number)}
                />
              </Form.Item>
            </>
          </>
        }

        {
          userStartDate 
          && !isBefore(new Date(userStartDate as string), new Date(period?.start as string)) 
          && locationResetQuotas === 'FISCAL_YEAR'
          && isInitialPeriod
          && !hasUnlimitedDays 
          && leaveType.accrualType === AccrualTypeEnum.none
          && <Form.Item
            label={
              <IntlMessages
                id="components.userLeaveTypeForm.prorateLabel"
                values={{
                  startDate: `${dayjs(userStartDate)
                    .utc()
                    .locale(locale as string)
                    .format('MMMM D, YYYY')}`,
                }}
              />
            }
            name="shouldProrate"
            {...formItemLayout}
          >
            <Radio.Group
              onChange={onChangeShouldProrate}
              options={[
                { label: <IntlMessages id="app.yes" />, value: true },
                { label: <IntlMessages id="app.no" />, value: false },
              ]}
              optionType="button"
            />
          </Form.Item>
        }

        <Form.Item
          label={
            <>
              <IntlMessages id="app.timeOffInLieu" />
              <Tooltip className='info-tooltip' title={
                <IntlMessages id="app.timeOffInLieuTooltip" values={{
                  helpDesk: (...chunks) => <a href="https://vacationtracker.io/helpdesk/" target="_blank" rel="noopener noreferrer">{chunks}</a>,
                }} />
              }><InfoCircleOutlined /></Tooltip>
            </>
          }
          name="toilDays"
          {...formItemLayout}
        >
          <InputNumber
            precision={2}
            style={{width: '140px'}}
            min={0}
            max={hourlyLeaveAccounting ? 2920 : 365}
            addonAfter={<Text><IntlMessages id={`app.${hourlyLeaveAccounting ? 'hours' : 'days'}`}/></Text>}
            disabled={true}
          />
        </Form.Item>

      </Form>
      {showQuotaAdditionalSummary &&
        <>
          <Divider/>
          <Alert
            message={<IntlMessages
              id={hasUnlimitedDays ? 'components.userLeaveTypeForm.quotaAdditionalSummaryUnlimited' : 'components.userLeaveTypeForm.quotaAdditionalSummary'}
              values={{
                user: userName,
                leaveTypeName: leaveType.leaveTypeName,
                total: hasUnlimitedDays 
                  ? <IntlMessages id="app.unlimited"/>
                  : roundTo2Decimals(yearlyQuota + seniorityEntitlementPerYear + entitlementByRoleForYear),
                future: applyToFutureYears ? 1 : 2, //plural for trans
                hourlyLeaveAccounting: hourlyLeaveAccounting ? 1 : 2, //plural for trans
                prorateText: locationResetQuotas === 'FISCAL_YEAR' && userStartDate && shouldProrate && period?.start && period?.end && form.getFieldsValue(['shouldProrate']) &&
                  <IntlMessages
                    id={
                      hourlyLeaveAccounting
                        ? 'components.userLeaveTypeForm.prorateInfoHours'
                        : 'components.userLeaveTypeForm.prorateInfoDays'
                    }
                    values={{
                      ...getProratedPreciseQuota(!hourlyLeaveAccounting),
                      user: userName,
                      yearPeriod: `${dayjs(period.start)
                        .utc()
                        .locale(locale as string)
                        .format('MMMM D, YYYY')} - ${dayjs(period.end)
                        .utc()
                        .locale(locale as string)
                        .format('MMMM D, YYYY')}`,
                      fullQuota: yearlyQuota,
                      startOfNextYear: dayjs(period.start).add(1, 'year').utc().locale(locale as string).format('MMMM D, YYYY'),
                    }}
                  />,
              }}
            />}
            type="info"
            style={{ marginBottom: '20px' }}
          />
        </>
      }
    </Modal>
  )
}

export default UserLeaveTypeForm
